/**
 * Shared types and constants for private lesson booking flow.
 * Used by HybridCalendar (slot selection) and confirm page (display/pricing).
 *
 * REQUIREMENTS:
 * - Keep single-slot, manual multi-slot, and weekly block booking data shapes
 *   aligned so all paths reuse the same hold and batch checkout behavior.
 * - Keep until-canceled recurring selections separate from finite batch blocks
 *   so recurring setup can call the series API without exceeding cart limits.
 * - Recurring "Until canceled" lessons are intentionally a single-confirmation
 *   parent flow. There is NO per-week preview list — the gym owns conflict
 *   resolution for any future generated week (plan U5, 2026-05-19 revision).
 */

import { defaultCreditCostForDuration } from '@/utils/private-lessons/durationOptions';

export interface AvailableSlot {
  id: string;
  coach_id: string;
  coach_name: string;
  coach_profile_image_url?: string | null;
  coach_gender?: string | null;
  coach_gender_label?: string | null;
  gym_location_id?: string;
  gym_location_name?: string;
  date: string;
  start_time: string;
  end_time: string;
  duration: number; // minutes — any active program duration option (15-120 in 5-min steps)
  price: number;
  /**
   * False while `price` is only a placeholder (a cart slot restored from a hold
   * before availability loads). A placeholder 0 is NOT a free lesson (#4968).
   */
  price_loaded?: boolean;
  /** Lesson-pack credits one booking of this slot consumes (from the duration option row). */
  credit_cost?: number;
  skill_level_min?: number;
  skill_level_max?: number;
}

export const MAX_BATCH_SLOTS = 10;

export const WEEKLY_BLOCK_WEEK_OPTIONS = [4, 6, 8, 10] as const;

/**
 * available — the same weekly time is open.
 * resolved  — the parent picked another time with the same coach that week.
 * skipped   — no time that week; the block adds a week at the end instead (#6221).
 */
export type WeeklyBlockCandidateStatus = 'available' | 'resolved' | 'skipped';

export interface WeeklyBlockCandidate {
  weekIndex: number;
  targetDate: string;
  targetSlot: AvailableSlot | null;
  replacementSlot: AvailableSlot | null;
  status: WeeklyBlockCandidateStatus;
  /** A week past the chosen count, added because an earlier week was skipped. */
  isAddedWeek: boolean;
}

/** A Gym Closings day range the available-slots route returns (#6221). */
export interface LessonClosingDays {
  name: string;
  start_date: string;
  end_date: string;
  location_id: string | null;
}

export interface WeeklyBlockContext {
  isWeeklyBlock: true;
  startSlotId: string;
  weekCount: number;
  generatedDates: string[];
  replacementSlotIds: string[];
  selectedSlotIds: string[];
}

/**
 * Server-bound recurring-series selection. Carries only the parent's chosen
 * starting slot plus the slot hold id (so the server can verify the slot is
 * still reserved when the parent submits the confirmation). The parent never
 * sees a per-week preview, so no preview fields are stored here.
 */
export interface RecurringPrivateLessonSelectionContext {
  isRecurringSeries: true;
  startSlot: AvailableSlot;
  selectedHoldId?: string | null;
}

const SESSION_STORAGE_KEY_PREFIX = 'pl-selected-slots-';
const HOLDS_SESSION_STORAGE_KEY_PREFIX = 'pl-slot-holds-';
const WEEKLY_BLOCK_CONTEXT_STORAGE_KEY_PREFIX = 'pl-weekly-block-context-';
const RECURRING_SERIES_CONTEXT_STORAGE_KEY_PREFIX = 'pl-recurring-series-context-';

export function getSessionStorageKey(programId: string): string {
  return `${SESSION_STORAGE_KEY_PREFIX}${programId}`;
}

/**
 * Session storage key for slot hold metadata. The cart uses this to carry
 * {slot_id -> { hold_id, expires_at }} from the booking page into the
 * confirm page without needing an extra round trip to `GET /slots/hold/mine`.
 */
export function getHoldsSessionStorageKey(programId: string): string {
  return `${HOLDS_SESSION_STORAGE_KEY_PREFIX}${programId}`;
}

export function getWeeklyBlockContextStorageKey(programId: string): string {
  return `${WEEKLY_BLOCK_CONTEXT_STORAGE_KEY_PREFIX}${programId}`;
}

export function getRecurringSeriesContextStorageKey(programId: string): string {
  return `${RECURRING_SERIES_CONTEXT_STORAGE_KEY_PREFIX}${programId}`;
}

/**
 * Where a cart lives in this tab. A staff "Preview as a parent" (#5634, KTD11)
 * keeps its own keys and no hold map, so it never reads, reuses or clears a
 * real cart the staff member has open for this program.
 */
export interface LessonCartStorageKeys {
  slots: string;
  /** null in preview: a preview places no hold. */
  holds: string | null;
  weeklyBlock: string;
  recurringSeries: string;
}

const PREVIEW_KEY_PREFIX = 'pl-preview-';

export function lessonCartStorageKeys(programId: string, preview: boolean): LessonCartStorageKeys {
  if (!preview) {
    return {
      slots: getSessionStorageKey(programId),
      holds: getHoldsSessionStorageKey(programId),
      weeklyBlock: getWeeklyBlockContextStorageKey(programId),
      recurringSeries: getRecurringSeriesContextStorageKey(programId),
    };
  }
  return {
    slots: `${PREVIEW_KEY_PREFIX}selected-slots-${programId}`,
    holds: null,
    weeklyBlock: `${PREVIEW_KEY_PREFIX}weekly-block-context-${programId}`,
    recurringSeries: `${PREVIEW_KEY_PREFIX}recurring-series-context-${programId}`,
  };
}

/** Finish preview and Exit clear only these keys, never the real cart. */
export function clearPreviewLessonCart(programId: string): void {
  const keys = lessonCartStorageKeys(programId, true);
  try {
    sessionStorage.removeItem(keys.slots);
    sessionStorage.removeItem(keys.weeklyBlock);
    sessionStorage.removeItem(keys.recurringSeries);
  } catch {
    // Private mode: nothing was stored.
  }
}

export interface SlotHoldInfo {
  hold_id: string;
  expires_at: string;  // ISO timestamptz
}

export type HoldsMap = Record<string, SlotHoldInfo>;

/**
 * A slot held by another parent, returned by /available-slots alongside
 * the main `slots` list. The UI renders these with a distinct "Just reserved"
 * visual so parents don't confuse them with available or conflicting slots.
 */
export interface ReservedSlot {
  coach_id: string;
  date: string;
  start_time: string;
  end_time: string;
  session_duration: number;
}

// Credit-pack cost of a slot for DISPLAY/estimate math. Prefer the option
// row's credit_cost — carried on the slot payload (available-slots attaches
// it) or looked up from the program's embedded duration_options by the
// caller. The duration-based default only covers stale cart payloads saved
// before per-option costs existed. The server never trusts this value: the
// charge/decrement paths re-resolve from the option row.
export function getCreditCost(
  duration: number,
  optionCreditCost?: number | string | null,
): number {
  const fromOption = Number.parseFloat(String(optionCreditCost ?? ''));
  if (Number.isFinite(fromOption) && fromOption > 0) return fromOption;
  return defaultCreditCostForDuration(Number(duration) || 60);
}
